import React, { useState, useEffect, useRef } from "react";
import styles from "./AddVendorPanel.module.css";
import WebService from "../Services/WebService";
import Swal from "sweetalert2";
import CrossButton from "../Customs/Button/CrossButton";

// --- Types ---
interface OptionType {
  id: number;
  name: string;
}
// interface RoleType {
//   role_id: number;
//   role_name: string;
// }
interface CategoryType {
  category_id: number;
  category_name: string;
}
interface Vendor {
  user_id: number;
  vendor_id: number;
  username: string;
  email: string;
  mobile_number: string;
  user_status: string;
  company_name: string;
  gst_number: string;
  address: {
    complete_address?: string;
    address?: string;
    landmark?: string;
    country?: number;
    state?: number;
    city?: number;
  }[];
  category_ids: number[] | null;
   raw_materials?: { category_id: number; category_name: string }[];
}
interface Props {
  onClose: () => void;
  onSuccess?: () => void;
  editVendor?: Vendor | null;
}

const DEFAULT_PASSWORD = "securepassword";
const DEFAULT_STATUS = "ACTIVE";
const DEFAULT_ADDRESS_TYPE = "GENERAL";

const AddVendorPanel: React.FC<Props> = ({ onClose, onSuccess, editVendor }) => {
  const [username, setUsername] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [gstNumber, setGstNumber] = useState("");

  // --- Categories ---
  const [category_ids, setCategoryIds] = useState<number[]>([]);
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [categorySearch, setCategorySearch] = useState<string>("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Address
  const [address, setAddress] = useState("");
  const [landmark, setLandmark] = useState("");
  const [countries, setCountries] = useState<OptionType[]>([]);
  const [states, setStates] = useState<OptionType[]>([]);
  const [cities, setCities] = useState<OptionType[]>([]);
  const [countryId, setCountryId] = useState<number | "">("");
  const [stateId, setStateId] = useState<number | "">("");
  const [cityId, setCityId] = useState<number | "">("");

  // Rest
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [apiError, setApiError] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [initialLocation, setInitialLocation] = useState<{ state?: number; city?: number }>({});

useEffect(() => {
  if (editVendor) {
    setUsername(editVendor.username || "");
    setCompanyName(editVendor.company_name || "");
    setEmail(editVendor.email || "");
    setMobileNumber(editVendor.mobile_number || "");
    setGstNumber(editVendor.gst_number || "");


    if (Array.isArray(editVendor.raw_materials) && editVendor.raw_materials.length > 0) {
      setCategoryIds(editVendor.raw_materials.map((cat: any) => cat.category_id));
      setCategories(editVendor.raw_materials.map((cat: any) => ({
        category_id: cat.category_id,
        category_name: cat.category_name,
      })));
    } else {
      setCategoryIds([]);
      setCategories([]);
    }

    const a = editVendor.address?.[0] || {};
    setAddress(a.address || "");
    setLandmark(a.landmark || "");
    setCountryId(a.country ?? "");
    setInitialLocation({
      state: a.state ?? undefined,
      city: a.city ?? undefined,
    });
    // Do NOT setStateId or setCityId here
  } else {
    setUsername("");
    setCompanyName("");
    setEmail("");
    setMobileNumber("");
    setGstNumber("");
    setCategoryIds([]);
    setCategories([]);
    setAddress("");
    setLandmark("");
    setCountryId("");
    setStateId("");
    setCityId("");
    setInitialLocation({});
  }
}, [editVendor]);


// This will run whenever states are fetched/changed
useEffect(() => {
  if (states.length && initialLocation.state) {
    setStateId(initialLocation.state);
    setInitialLocation(loc => ({ ...loc, state: undefined }));
  }
}, [states, initialLocation]);

// This will run whenever cities are fetched/changed
useEffect(() => {
  if (cities.length && initialLocation.city) {
    setCityId(initialLocation.city);
    setInitialLocation(loc => ({ ...loc, city: undefined }));
  }
}, [cities, initialLocation]);

  useEffect(() => {
    WebService.getAPI<any>({ action: "/v1/door-management/users/countries" })
      .then((res) => {
        const arr = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
        setCountries(
          arr.map((c: any) => ({
            id: c.id ?? c.country_id ?? 0,
            name: c.name ?? c.country_name ?? "",
          }))
        );
      })
      .catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    if (!countryId) {
      setStates([]);
      setStateId("");
      setCities([]);
      setCityId("");
      return;
    }
    WebService.getAPI<any>({
      action: `/v1/door-management/users/countries/${countryId}/states`,
    })
      .then((res) => {
        const arr = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
        setStates(
          arr.map((s: any) => ({
            id: s.id ?? s.state_id ?? 0,
            name: s.name ?? s.state_name ?? "",
          }))
        );
        setStateId("");
        setCities([]);
        setCityId("");
      })
      .catch(() => {
        setStates([]);
        setStateId("");
        setCities([]);
        setCityId("");
      });
  }, [countryId]);

  useEffect(() => {
    if (!stateId) {
      setCities([]);
      setCityId("");
      return;
    }
    WebService.getAPI<any>({
      action: `/v1/door-management/users/states/${stateId}/cities`,
    })
      .then((res) => {
        const arr = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
        setCities(
          arr.map((c: any) => ({
            id: c.id ?? c.city_id ?? 0,
            name: c.name ?? c.city_name ?? "",
          }))
        );
        setCityId("");
      })
      .catch(() => {
        setCities([]);
        setCityId("");
      });
  }, [stateId]);


useEffect(() => {
  // On mount (or editVendor changes), fetch all categories once
  WebService.getAPI<any>({
    action: `/v1/door-management/raw-material/categories?type=NO_SUB_CATEGORY`,
  })
    .then((res) => {
      const arr = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];
      setCategories(
        arr.map((cat: any) => ({
          category_id: cat.category_id,
          category_name: cat.category_name,
        }))
      );
    })
    .catch(() => setCategories([]));
}, []);

  // After categories are fetched, patch in any missing editVendor categories
useEffect(() => {
  if (
    editVendor &&
    Array.isArray(editVendor.category_ids) &&
    editVendor.category_ids.length > 0
  ) {
    const missingIds = editVendor.category_ids.filter(
      (id) => !categories.some((cat) => cat.category_id === id)
    );
    if (missingIds.length > 0) {
      // Fetch all missing categories in parallel
      Promise.all(
        missingIds.map((id) =>
          WebService.getAPI({
            action: `/v1/door-management/raw-material/categories/${id}`,
          })
        )
      ).then((responses) => {
        // Flatten and filter valid categories
const found = (responses as { data: { data: any } }[])
  .map((r) => r.data?.data)
  .filter(Boolean)
  .map((cat: any) => ({
    category_id: cat.category_id,
    category_name: cat.category_name,
  }));

        // Only add categories that aren't already in categories
        setCategories((prev) => [
          ...prev,
          ...found.filter(
            (f) => !prev.some((c) => c.category_id === f.category_id)
          ),
        ]);
      });
    }
  }
  // eslint-disable-next-line
}, [categories, editVendor]);

  // Panel click outside to close
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setDropdownOpen(false);
    }
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);

  // Validation
  const validate = () => {
    const e: { [k: string]: string } = {};
    if (!username.trim()) e.username = "Name is required";
    if (!companyName.trim()) e.companyName = "Company is required";
    if (email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) e.email = "Valid email required";
    if (!mobileNumber.trim() || !/^[6-9]\d{9}$/.test(mobileNumber)) e.mobileNumber = "Valid mobile required";
    if (!category_ids.length) e.category_ids = "Select at least 1 category";
    if (!address.trim()) e.address = "Address required";
    if (!landmark.trim()) e.landmark = "Landmark required";
    if (!countryId) e.countryId = "Country required";
    if (!stateId) e.stateId = "State required";
    if (!cityId) e.cityId = "City required";
    setErrors(e);
    setApiError("");
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setApiError("");

    const payload: any = {
      username,
      company_name: companyName,
      email,
      gst_number: gstNumber,
      mobile_number: mobileNumber,
      password: DEFAULT_PASSWORD,
      user_status: DEFAULT_STATUS,
      role_id: 7,
      address: {
        address_type: DEFAULT_ADDRESS_TYPE,
        address,
        landmark,
        country: countryId,
        state: stateId,
        city: cityId,
      },
      category_ids: category_ids,
    };

    try {
      if (editVendor) {
        // --- EDIT mode: PUT ---
        const res = await WebService.putAPI({
          action: `/v1/door-management/vendors/${editVendor.user_id}`,
          body: payload,
        }) as { status_code: number; message?: string };

        if (res.status_code === 200) {
          Swal.fire({
            icon: "success",
            title: "Vendor updated!",
            timer: 1500,
            showConfirmButton: false,
          });
          setTimeout(() => {
            onClose();
            if (onSuccess) onSuccess();
          }, 1550);
        } else {
          setApiError(res.message || "Failed to update vendor. Please check the details.");
        }
      } else {
        // --- ADD mode: POST ---
        const res = await WebService.postAPI<any>({
          action: "/v1/door-management/vendors",
          body: payload,
        });
        if (res.status_code === 201 || res.status_code === 200) {
          Swal.fire({ icon: "success", title: "Vendor Added!", timer: 1500, showConfirmButton: false });
          setTimeout(() => {
            onClose();
            if (onSuccess) onSuccess();
          }, 1550);
        } else {
          setApiError(res.message || "Failed to add vendor. Please check the details.");
        }
      }
    } catch (err: any) {
      setApiError(err?.message || "Something went wrong! Please try again.");
    }
    setLoading(false);
  };

  const selectedCategories = categories.filter(cat => category_ids.includes(cat.category_id));


  return (
    <div className={styles.overlay}>
      {apiError && (
        <div style={{
          position: "fixed", top: 22, right: "calc(50vw - 250px)", left: 0, zIndex: 99999,
          background: "#fffbe7", color: "#e43e2d", fontWeight: 600,
          border: "1.3px solid #FFD400", borderRadius: 9, padding: "12px 24px",
          margin: "0 auto", width: 440, maxWidth: "calc(100vw - 50px)", textAlign: "center",
          fontSize: 16, boxShadow: "0 4px 18px 0 rgba(41,41,41,0.08)"
        }}>
          {apiError}
        </div>
      )}
      <div className={styles.panel} ref={panelRef}>
        <div className={styles.header}>
          <div>{editVendor ? "Edit Vendor" : "Add New Vendor"}</div>
          <CrossButton onClick={onClose} className={styles.closeBtn} />
        </div>
        <form className={styles.form} onSubmit={handleSubmit} autoComplete="off">
          {/* 2-col grid */}
          <div className={styles.row}>
            <div className={styles.inputGroup}>
              <label>Name*</label>
              <input
                className={errors.username ? styles.error : ""}
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="Vendor Name"
                autoFocus
              />
              {errors.username && <span className={styles.errMsg}>{errors.username}</span>}
            </div>
            <div className={styles.inputGroup}>
              <label>Company Name*</label>
              <input
                className={errors.companyName ? styles.error : ""}
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder="Company Name"
              />
              {errors.companyName && <span className={styles.errMsg}>{errors.companyName}</span>}
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.inputGroup}>
              <label>Email (Optional)</label>
              <input
                className={errors.email ? styles.error : ""}
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Email"
                type="email"
              />
              {errors.email && <span className={styles.errMsg}>{errors.email}</span>}
            </div>
            <div className={styles.inputGroup}>
              <label>Mobile Number*</label>
              <input
                className={errors.mobileNumber ? styles.error : ""}
                value={mobileNumber}
                onChange={e => setMobileNumber(e.target.value)}
                placeholder="Mobile Number"
                maxLength={10}
              />
              {errors.mobileNumber && <span className={styles.errMsg}>{errors.mobileNumber}</span>}
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.inputGroup}>
              <label>GST Number (Optional)</label>
              <input
                value={gstNumber}
                onChange={e => setGstNumber(e.target.value)}
                placeholder="GST Number"
                maxLength={15}
              />
            </div>
          </div>

          {/* Category Multi-Select as Chips and Dropdown */}
          <div className={styles.inputGroup} ref={dropdownRef}>
            <label>Categories*</label>
            <div className={styles.selectedChips}>
              {selectedCategories.map(cat => (
                <span key={cat.category_id} className={styles.chip}>
                  {cat.category_name}
                  <span
                    className={styles.chipDelete}
                    onClick={() => setCategoryIds(ids => ids.filter(id => id !== cat.category_id))}
                    title="Remove"
                  >
                    ×
                  </span>
                </span>
              ))}
            </div>
            <div className={styles.dropdownWrapper}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search categories..."
                value={categorySearch}
                onFocus={() => setDropdownOpen(true)}
                onChange={e => { setCategorySearch(e.target.value); setDropdownOpen(true); }}
              />
              {dropdownOpen && (
                <div className={styles.dropdownList}>
                  {categories
                    .filter(cat => !category_ids.includes(cat.category_id))
                    .filter(cat => cat.category_name.toLowerCase().includes(categorySearch.toLowerCase()))
                    .map(cat => (
                      <div
                        key={cat.category_id}
                        className={styles.dropdownItem}
                        onClick={() => {
                          setCategoryIds(ids => [...ids, cat.category_id]);
                          setCategorySearch("");
                          setDropdownOpen(false);
                        }}
                      >
                        {cat.category_name}
                      </div>
                    ))}
                  {categories.filter(cat => !category_ids.includes(cat.category_id)).length === 0 && (
                    <div className={styles.dropdownItem} style={{ color: "#aaa" }}>No more categories</div>
                  )}
                </div>
              )}
            </div>
            {errors.category_ids && <span className={styles.errMsg}>{errors.category_ids}</span>}
          </div>

          <div className={styles.row}>
            <div className={styles.inputGroup}>
              <label>Address*</label>
              <input
                className={errors.address ? styles.error : ""}
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder="e.g. Street Address"
              />
              {errors.address && <span className={styles.errMsg}>{errors.address}</span>}
            </div>
            <div className={styles.inputGroup}>
              <label>Landmark*</label>
              <input
                className={errors.landmark ? styles.error : ""}
                value={landmark}
                onChange={e => setLandmark(e.target.value)}
                placeholder="Near City Mall"
              />
              {errors.landmark && <span className={styles.errMsg}>{errors.landmark}</span>}
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.inputGroup}>
              <label>Country*</label>
              <select
                className={errors.countryId ? styles.error : ""}
                value={countryId}
                onChange={e => setCountryId(Number(e.target.value))}
              >
                <option value="">Select Country</option>
                {countries.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {errors.countryId && <span className={styles.errMsg}>{errors.countryId}</span>}
            </div>
            <div className={styles.inputGroup}>
              <label>State*</label>
              <select
                className={errors.stateId ? styles.error : ""}
                value={stateId}
                onChange={e => setStateId(Number(e.target.value))}
                disabled={!states.length}
              >
                <option value="">Select State</option>
                {states.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              {errors.stateId && <span className={styles.errMsg}>{errors.stateId}</span>}
            </div>
            <div className={styles.inputGroup}>
              <label>City*</label>
              <select
                className={errors.cityId ? styles.error : ""}
                value={cityId}
                onChange={e => setCityId(Number(e.target.value))}
                disabled={!cities.length}
              >
                <option value="">Select City</option>
                {cities.map(ci => (
                  <option key={ci.id} value={ci.id}>{ci.name}</option>
                ))}
              </select>
              {errors.cityId && <span className={styles.errMsg}>{errors.cityId}</span>}
            </div>
          </div>

          <div className={styles.footer}>
            <button
              className={styles.cancelBtn}
              type="button"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button className={styles.submitBtn} type="submit" disabled={loading}>
              {loading ? (editVendor ? "Updating..." : "Adding...") : (editVendor ? "Update Vendor" : "Add Vendor")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddVendorPanel;
